<!--
 * @Descripttion: 选择场景弹窗（工业+服务商共用：左侧产品多选，右侧 XrkTable 表格勾选）
 * @Date: 2026-08-06
-->
<template>
  <BaseDialog
    class="scene-select-dialog"
    style="max-height: 98vh; width: 85%; height: 90vh"
    :model-value="true"
    title="选择场景"
    :show-close="true"
    :close-on-press-escape="true"
    :close-on-click-modal="false"
    :cancel-btn-config="{ show: true, content: '取消' }"
    :confirm-btn-config="{ show: true, content: '确定' }"
    :before-cancel="onCancel"
    :before-confirm="onConfirm"
  >
    <div class="scene-dialog-content">
      <!-- 左侧：产品选择列表 -->
      <aside class="product-panel">
        <BaseAffix :offset="affixOffset" class="left-affix">
          <div class="panel-head">
            <span class="panel-title">选择产品</span>
            <!-- 仅 IAS 工业角色：新增产品入口（跳产品库） -->
            <BaseButton
              v-if="isIndustry"
              link
              type="primary2"
              @click="onAddProduct"
            >
              新增产品
            </BaseButton>
          </div>
          <input
            v-model="productQuery"
            class="product-search"
            placeholder="搜索产品名称、类别或属性"
          />

          <div class="product-list" v-xrk-loading="productLoading">
            <div
              v-for="product in filteredProducts"
              :key="product.productId"
              class="product-row"
              :class="{
                active: activeProductId === product.productId,
                checked: checkedMap.has(product.productId)
              }"
              @click="onSwitchProduct(product.productId)"
            >
              <input
                type="checkbox"
                :checked="checkedMap.has(product.productId)"
                @click.stop
                @change="onToggleProduct(product.productId)"
              />
              <div class="product-info">
                <div class="product-name">{{ product.productName }}</div>
                <div class="product-sub">
                  {{
                    [
                      product.productCategory,
                      product.productAttribute,
                      product.productType == 2 ? '' : product.lifecycleStage
                    ]
                      .filter(Boolean)
                      .join(' / ')
                  }}
                </div>
              </div>
              <div class="product-side">
                <span
                  v-if="product.available === 0"
                  class="product-unavailable"
                >
                  不可使用
                </span>
                <span
                  v-if="getProductSceneCount(product.productId)"
                  class="product-count"
                >
                  已选 {{ getProductSceneCount(product.productId) }}
                </span>
              </div>
            </div>
            <div v-if="filteredProducts.length === 0" class="product-empty">
              未找到匹配产品
            </div>
          </div>
        </BaseAffix>
      </aside>

      <!-- 右侧：场景表格 -->
      <section class="scene-panel">
        <!-- 未选产品时提示 -->
        <div v-if="!activeProduct" class="scene-empty-hint">
          <b>请选择左侧产品</b>
          <div class="scene-product-meta">
            选择产品后，右侧展示根据产品属性匹配的场景。
          </div>
        </div>

        <!-- 产品信息 -->
        <div v-else class="scene-toolbar">
          <div class="scene-product-name">
            {{ activeProduct.productName }}
          </div>
          <div class="scene-product-meta">
            产品类别：
            <b>{{ activeProduct.productCategory || '—' }}</b>
            产品属性：
            <b>{{ activeProduct.productAttribute || '—' }}</b>
            <template v-if="activeProduct.productType != 2">
              产品生命周期：
              <b>{{ activeProduct.lifecycleStage || '—' }}</b>
            </template>
          </div>
          <div class="scene-toolbar-tip">
            已基于当前产品信息为你推荐适用场景，请选择。
          </div>
        </div>

        <!-- XrkTable 表格 -->
        <div class="scene-table-wrap" v-xrk-loading="sceneLoading">
          <XrkTable
            ref="xrkTableRef"
            :getListAjax="getListAjax"
            :searchConfig="searchConfig"
            :tableConfig="tableConfig"
            :paginationConfig="paginationConfig"
          />
        </div>
      </section>
    </div>
  </BaseDialog>
</template>

<script lang="tsx" setup>
import { ref, computed, reactive, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useDebounceFn } from '@vueuse/core';
import {
  BaseDialog,
  BaseButton,
  BaseToolTip,
  XrkTable,
  XrkTableProps,
  XrkTableRef,
  XrkMessage,
  useTableColumn,
  useSelectColumn,
  BaseAffix
} from 'xrk-components';
import { storeToRefs } from 'pinia';
import appStore from '@/store';
import { formatter } from '@/utils/';
import { getSceneProductList, getSceneSceneList } from '../data-source/apis';
import type {
  SceneManageProductItem,
  SceneManageProductBaseDTO,
  SceneManageSceneItem
} from '../data-source/types';

// ======================== Props & Emits ========================

const emit = defineEmits<{
  /** 确定：emit 勾选集合（SceneManageProductItem[]，scenes 只含勾选场景），调用层全量更新本地数据 */
  (e: 'confirm', products: SceneManageProductItem[]): void;
}>();

const props = withDefaults(
  defineProps<{
    remove?: () => void;
    /** 调用层传入的当前勾选数据（SceneManageProductItem[]，用于默认勾选 + 默认赋值） */
    products: SceneManageProductItem[];
    industryName?: string;
    industryId?: number;
    aspId?: number;
    platformId?: number;
    planId?: number;
  }>(),
  {
    remove() {
      /* auto-injected */
    },
    industryName: '',
    industryId: undefined,
    aspId: undefined,
    platformId: undefined,
    planId: undefined
  }
);

// ======================== 状态 ========================

/** 仅 IAS：工业角色（functionCode=1）显示「新增产品」入口 */
const router = useRouter();
const { info: userInfo } = storeToRefs(appStore.useUserStoreSetup);
const isIndustry = computed(() => userInfo.value?.functionCode === 1);

/** 左侧全量产品（V2 product/page 返回 SceneManageProductBaseDTO，无 scenes） */
const allProducts = ref<SceneManageProductBaseDTO[]>([]);
/** 产品列表加载中 */
const productLoading = ref(false);
/** 勾选集合：productId → SceneManageProductItem（scenes 只存勾选的场景，与 SceneManageProductItem 结构一致） */
const checkedMap = reactive(
  new Map<string, SceneManageProductItem>()
) as unknown as Map<string, SceneManageProductItem>;
/** 场景加载中 */
const sceneLoading = ref(false);
/** 当前产品场景筛选 options（接口返回字符串数组；remoteMethod 假远程从这取，保证下拉响应） */
const sceneOptions = reactive({
  sceneNameOptions: [] as string[],
  medicalRepTagOptions: [] as string[],
  terminalChannelOptions: [] as string[],
  serviceObjectOptions: [] as string[]
});
/** 首次加载标记（搜索刷新不重新激活/拉场景） */
const initialized = ref(false);
/** 初始化激活：有已勾选产品时默认激活第一个已勾选产品，否则激活第一个产品 */
const activeProductId = ref<string>('');
/** 左侧产品本地搜索关键词 */
const productQuery = ref('');

/**
 * BaseAffix 固定偏移：左侧面板头部固定时距视口顶部的距离
 * 弹窗实际高度 = min(85vh, 650px)（xrk-components dialog 内置 max-height）
 * 实测：topGap 即为正确偏移，无需再加 dialog header 高度
 */
const affixOffset = computed(() => {
  const vh = window.innerHeight || 900;
  const dialogH = Math.min(vh * 0.85, 650);
  const topGap = Math.max((vh - dialogH) / 2, 0);
  return Math.round(topGap);
});

const xrkTableRef = ref<XrkTableRef>(null);

// ======================== 初始化 ========================

/** 加载左侧产品列表（V2 product/page：固定 pageSize=100 + productName 远程搜索）+ 用 props 勾选集合初始化默认勾选/赋值 */
const loadProducts = async () => {
  productLoading.value = true;
  try {
    const res = await getSceneProductList({
      industryId: props.industryId || undefined,
      productName: productQuery.value.trim() || undefined,
      pageNum: 1,
      pageSize: 100
    });
    allProducts.value = res?.data?.page?.list || [];
    // 用调用层传入的勾选集合做默认勾选（深拷贝隔离，取消不影响父组件）
    props.products.forEach(p => {
      checkedMap.set(p.productId, JSON.parse(JSON.stringify(p)));
    });
    // detail 的 available 优先于 product/page：把已配置产品的可用状态覆盖到左侧产品
    const detailAvailable = new Map(
      props.products.map(p => [p.productId, p.available])
    );
    allProducts.value.forEach(product => {
      const av = detailAvailable.get(product.productId);
      if (av !== undefined) product.available = av;
    });
    // 已勾选产品可能不在前 100 条内（产品多时）：合并进左侧列表；已勾选产品一律置顶展示
    const checkedIds = new Set(allProducts.value.map(p => p.productId));
    props.products.forEach(p => {
      if (!checkedIds.has(p.productId)) {
        allProducts.value.push({
          productId: p.productId,
          productName: p.productName || '',
          approvalNumber: p.approvalNumber || '',
          manufacturingEnterprise: p.manufacturingEnterprise || '',
          dosageForm: p.dosageForm || '',
          indication: p.indication || '',
          drugName: p.drugName || '',
          productCategory: p.productCategory || '',
          productAttribute: p.productAttribute || '',
          lifecycleStage: p.lifecycleStage || '',
          productSpec: p.productSpec || '',
          drugInstructions: p.drugInstructions || '',
          projectJustificationUrl: p.projectJustificationUrl || '',
          dictionariesName: (p as any).dictionariesName || '',
          departments: (p as any).departments || '',
          productType: p.productType,
          available: (p as any).available
        });
      }
    });
    // 已勾选产品置顶（勾选集合的产品排最前，其余按接口顺序）
    allProducts.value.sort((a, b) => {
      const aChecked = checkedMap.has(a.productId) ? 0 : 1;
      const bChecked = checkedMap.has(b.productId) ? 0 : 1;
      return aChecked - bChecked;
    });
    // 首次加载：默认激活第一个已勾选产品（否则第一个产品）+ 加载场景；
    // 搜索刷新：激活产品不变（已勾选产品置顶始终可见），场景数据无需重拉
    if (!initialized.value) {
      initialized.value = true;
      const firstChecked = [...checkedMap.keys()][0];
      activeProductId.value =
        firstChecked || allProducts.value[0]?.productId || '';
      if (activeProductId.value) {
        xrkTableRef.value?.initList?.();
      }
    }
  } catch (error) {
    console.error('加载产品列表失败:', error);
    XrkMessage.error('获取产品列表失败');
  } finally {
    productLoading.value = false;
  }
};

/** 激活产品时加载场景：由 XrkTable getListAjax 远程分页承担（每次翻页/搜索调接口） */

onMounted(() => {
  loadProducts();
});

/** 产品搜索：输入防抖 300ms 远程搜索（V2 product/page productName 参数，useDebounceFn） */
const debouncedLoadProducts = useDebounceFn(loadProducts, 300);

watch(productQuery, () => {
  debouncedLoadProducts();
});

// ======================== 计算属性 ========================

const activeProduct = computed(
  () =>
    allProducts.value.find(p => p.productId === activeProductId.value) ||
    // 搜索后激活产品可能不在结果列表（非勾选产品）：用勾选集合兜底，避免空态误判
    checkedMap.get(activeProductId.value) ||
    null
);

/** 左侧产品列表（接口已按 productName 远程过滤，直接展示） */
const filteredProducts = computed(() => allProducts.value);

// ======================== 搜索配置 ========================

const paginationConfig = {
  paginationPageSize: 15
};

/** sceneOptions 字段与下拉 prop 映射 */
const SCENE_OPTIONS_KEY: Record<string, string> = {
  sceneName: 'sceneNameOptions',
  medicalRepTag: 'medicalRepTagOptions',
  terminalChannelText: 'terminalChannelOptions',
  serviceObjectText: 'serviceObjectOptions'
};

/**
 * 场景筛选下拉假远程数据源：不调接口，从 sceneOptions（getListAjax 接口返回时更新）取选项，
 * 按接口数据结构 { data: { list } } 返回，解决 options prop 配置后不响应的问题（remote 每次执行取最新数据）
 */
const buildSceneRemote = (field: string) => (val?: string) => {
  const key = SCENE_OPTIONS_KEY[field];
  // 展示截断：默认只返回前 100 条（全量 options 上千条时下拉渲染卡顿）；
  // 搜索全量：关键词从全量数组过滤（BaseSelect 内部还会按 label 二次过滤，双保险）
  const list = ((sceneOptions as any)[key] || [])
    .filter((v: string) => v !== '全部')
    .filter((v: string) => !val || v.includes(val))
    .slice(0, 100)
    .map((v: string) => ({ [field]: v }));
  return Promise.resolve({ data: { list } });
};

const searchConfig = computed<XrkTableProps['searchConfig']>(() => {
  return {
    // 切换搜索/翻页时保留已勾选行（XrkTable 默认搜索会清空勾选）
    reserveSelection: true,
    searchColumns: [
      useSelectColumn({
        prop: 'sceneName',
        label: '场景名称',
        config: {
          placeholder: '全部',
          clearable: true,
          filterUseRemote: true,
          init: true,
          labelKey: 'sceneName',
          valueKey: 'sceneName',
          remoteMethod: buildSceneRemote('sceneName')
        }
      }),
      useSelectColumn({
        prop: 'medicalRepTag',
        label: '备案类型',
        config: {
          placeholder: '全部',
          clearable: true,
          filterUseRemote: true,
          init: true,
          labelKey: 'medicalRepTag',
          valueKey: 'medicalRepTag',
          remoteMethod: buildSceneRemote('medicalRepTag')
        }
      }),
      useSelectColumn({
        prop: 'terminalChannelText',
        label: '终端渠道',
        config: {
          placeholder: '全部',
          clearable: true,
          filterUseRemote: true,
          init: true,
          labelKey: 'terminalChannelText',
          valueKey: 'terminalChannelText',
          remoteMethod: buildSceneRemote('terminalChannelText')
        }
      }),
      useSelectColumn({
        prop: 'serviceObjectText',
        label: '适用服务对象',
        config: {
          placeholder: '全部',
          clearable: true,
          filterUseRemote: true,
          init: true,
          labelKey: 'serviceObjectText',
          valueKey: 'serviceObjectText',
          remoteMethod: buildSceneRemote('serviceObjectText')
        }
      })
    ],
    otherComponents: [
      {
        render: () => (
          <span class="scene-batch-btns">
            <BaseButton onClick={() => onSelectAll(true)}>全选</BaseButton>
            <BaseButton onClick={() => onSelectAll(false)}>清空</BaseButton>
          </span>
        )
      }
    ]
  };
});

// ======================== 表格配置 ========================

const formatPrice = (price?: number) => {
  if (price == null) return '--';
  // 金额已在 translators 层分→元（scenePageToYuan），直接展示
  return String(price);
};

const tableConfig = computed<XrkTableProps['tableConfig']>(() => ({
  baseConfig: {
    rowKey: '_id',
    // 空态：未选产品时提示选药，已选产品无匹配场景时提示无结果（需求 HTML：colspan 空行文案）
    emptyText: activeProduct.value
      ? '暂无符合条件的场景'
      : '请先选择产品，系统将根据产品属性匹配可选场景'
  },
  tableColumns: [
    useTableColumn({
      type: '_selection',
      prop: '_id',
      width: 90,
      fixed: 'left',
      // 勾选/取消场景后同步勾选集合（产品自动勾选 UI + 已选计数实时更新）
      selectionAddAfter: (row: any) => {
        syncSceneChecked(row, true);
      },
      selectionRemoveAfter: (row: any) => {
        syncSceneChecked(row, false);
      },
      // 当页全选/取消全选：XrkTable 内部只更新 store 不触发 After 钩子（左侧已选 N 不同步），
      // 手动拿当页数据同步 checkedMap 后再执行默认逐行勾选/取消逻辑
      selectionAllBefore: (done: Function) => {
        const pageRows = getPageRows();
        const rows = xrkTableRef.value?.getSelectionRows?.() || [];
        // 方向判断与 XrkTable 内部 checkboxAllChecked 一致：当页全部已勾 → 本次点击为取消
        const allChecked =
          pageRows.length > 0 &&
          pageRows.every((r: any) => rows.some((s: any) => s._id === r._id));
        pageRows.forEach((r: any) => syncSceneChecked(r, !allChecked));
        done();
      },
      // 表头「已选 N 项」自定义：只显示当前页已勾选数（内置默认 = 全局 selectionIds 总数，跨页懒加载回显导致翻页跳变）
      renderHeaderSelectionLabel: () => {
        const rows = xrkTableRef.value?.getSelectionRows?.() || [];
        const ids = new Set(rows.map((r: any) => r._id));
        const count = getPageRows().filter((r: any) => ids.has(r._id)).length;
        return count > 0 ? `已选 ${count} 项` : '';
      }
    }),
    useTableColumn({
      label: '推荐场景',
      prop: 'sceneName',
      width: 280,
      formatter: ({ cellValue }: { cellValue: string }) => cellValue || '--'
    }),
    useTableColumn({
      label: '是否备案建议',
      prop: 'medicalRepTag',
      width: 120,
      formatter: ({ cellValue }: { cellValue: string }) =>
        formatter.defaultFormat(cellValue)
    }),
    useTableColumn({
      label: '终端渠道/服务对象',
      prop: 'terminalChannelText',
      minWidth: 160,
      render: ({ row }: { row: SceneManageSceneItem }) => (
        <div class="channel-object-cell">
          <div>终端渠道：{row.terminalChannelText || '-'}</div>
          <div class="channel-object-sub">
            服务对象：{row.serviceObjectText || '-'}
          </div>
        </div>
      )
    }),
    useTableColumn({
      label: '服务简介',
      prop: 'projectIntroduction',
      minWidth: 200,
      render: ({ row }: { row: SceneManageSceneItem }) => (
        <div class="intro-cell">
          <span class="intro-text">{row.projectIntroduction || '--'}</span>
          {row.projectIntroduction && (
            <BaseToolTip
              content={row.projectIntroduction}
              placement="top"
            ></BaseToolTip>
          )}
        </div>
      )
    }),
    useTableColumn({
      label: '推荐理由',
      prop: 'sceneObjective',
      minWidth: 180,
      formatter: ({ cellValue }: { cellValue: string }) => cellValue || '-'
    }),
    useTableColumn({
      label: '输出成果',
      prop: 'coreKpi',
      minWidth: 160,
      formatter: ({ cellValue }: { cellValue: string }) => cellValue || '-'
    }),
    useTableColumn({
      label: '历史执行',
      prop: 'executeProjectCount',
      width: 170,
      render: ({ row }: { row: SceneManageSceneItem }) => {
        const count = row.executeProjectCount || 0;
        if (count > 0) {
          return (
            <div class="history-cell">
              <span class="history-badge">已执行 {count} 次</span>
              <span class="history-settle">
                累计结算 ￥{formatPrice(row.cumulativeSettlementAmount)}
              </span>
            </div>
          );
        }
        return <span class="history-empty">—</span>;
      }
    }),
    useTableColumn({
      label: '平台价格区间',
      prop: 'platformPriceLower',
      width: 150,
      render: ({ row }: { row: SceneManageSceneItem }) => (
        <span>
          {formatPrice(row.platformPriceLower)} ~{' '}
          {formatPrice(row.platformPriceUpper)} 元
        </span>
      )
    })
  ]
}));

// ======================== 表格数据源（远程分页） ========================

const getListAjax = async (params?: Record<string, any>) => {
  const {
    sceneName,
    medicalRepTag,
    terminalChannelText,
    serviceObjectText,
    pageNum = 1,
    pageSize = 15
  } = params || {};

  // 未激活产品：空数据（未选产品提示由模板 scene-empty-hint 承担）
  if (!activeProductId.value) {
    return { data: { list: [], total: 0 } };
  }

  // 当前激活产品：把产品的三个属性传给场景接口（匹配覆盖值，无值传 undefined 不序列化）
  const activeProd = allProducts.value.find(
    p => p.productId === activeProductId.value
  );
  const res = await getSceneSceneList({
    productId: activeProductId.value,
    planId: props.planId || undefined,
    industryId: props.industryId || undefined,
    aspId: props.aspId || undefined,
    sceneName: sceneName || undefined,
    terminalChannelText: terminalChannelText || undefined,
    serviceObjectText: serviceObjectText || undefined,
    medicalRepTag: medicalRepTag || undefined,
    productCategory: activeProd?.productCategory || undefined,
    productAttribute: activeProd?.productAttribute || undefined,
    lifecycleStage: activeProd?.lifecycleStage || undefined,
    pageNum,
    pageSize
  });
  const data = res?.data;
  // 4 个搜索项 options：接口返回的筛选结果选项（remoteMethod 假远程从这里取）
  sceneOptions.sceneNameOptions = data?.sceneNameOptions || [];
  sceneOptions.medicalRepTagOptions = data?.medicalRepTagOptions || [];
  sceneOptions.terminalChannelOptions = data?.terminalChannelOptions || [];
  sceneOptions.serviceObjectOptions = data?.serviceObjectOptions || [];

  // 勾选集合合并：跨页勾选回显（checkedMap 场景命中标记 selected + 默认赋值字段覆盖；_id 唯一辨识）
  const checkedProduct = checkedMap.get(activeProductId.value);
  const list = (data?.scenePage?.list || []).map(scene => {
    // _id = 产品id_场景id（接口对齐返回，前端兜底构造）
    const _id = scene._id || `${activeProductId.value}_${scene.sceneId}`;
    const checked = (checkedProduct?.scenes || []).find(
      s => s.sceneId === scene.sceneId
    );
    const row = checked
      ? { ...scene, _id, ...checked, selected: true }
      : { ...scene, _id, selected: false };
    // 已勾选场景回显到 XrkTable selectionIds（跨页保留，重复 add 去重无害）
    if (row.selected) {
      xrkTableRef.value?.addSelectionRow?.(_id, row);
    }
    return row;
  });

  return { data: { list, total: data?.scenePage?.total || 0 } };
};

// ======================== 方法 ========================

/** 仅 IAS 工业角色：新增产品（关弹窗 → 跳产品库） */
const onAddProduct = async () => {
  try {
    await router.push({ name: 'product-mgmt-library' });
    props.remove();
  } catch (e) {
    XrkMessage.error('无产品库菜单权限');
  }
};

/** 当前页行数据（getTableData 返回 ref 需解包，use-table.tsx pageAllData = ref([])） */
const getPageRows = (): any[] => {
  const raw = xrkTableRef.value?.getTableData?.();
  return ((Array.isArray(raw) ? raw : raw?.value) ?? []) as any[];
};

/** 产品下已选场景数（左侧「已选 N」：checkedMap 响应式，实时准确不依赖激活/加载） */
const getProductSceneCount = (productId: string) => {
  const product = checkedMap.get(productId);
  if (!product) return 0;
  return (product.scenes || []).length;
};

/** 激活产品（勾选/点击均触发）：切换激活 + 清空筛选条件（场景数据由 getListAjax 远程分页拉取；各产品勾选独立保留，_id 前缀隔离） */
const onSwitchProduct = (productId: string) => {
  if (activeProductId.value === productId) return;
  activeProductId.value = productId;
  // 清空 store 勾选：表头「已选 N 项」= 全局 selectionIds 总数，跨产品残留会污染文案；
  // getListAjax 按 checkedMap 回显当前产品已勾选行，勾选数据不丢
  xrkTableRef.value?.clearSelectionRows?.();
  // 清空 store 勾选：表头「已选 N 项」= 全局 selectionIds 总数，跨产品残留会污染文案；
  // getListAjax 按 checkedMap 回显当前产品已勾选行，勾选数据不丢
  xrkTableRef.value?.clearSelectionRows?.();
  xrkTableRef.value?.clearSearchValues?.();
  xrkTableRef.value?.initList?.();
};

/** 勾选/取消产品：勾选=加入勾选集合 + 激活；取消=移出勾选集合 + 清场景 + 清该产品 selectionIds */
const onToggleProduct = (productId: string) => {
  const checked = checkedMap.get(productId);
  if (checked) {
    // 取消勾选：移出集合 + 清该产品已勾选场景的 selectionIds（_id 前缀 = productId_）
    checkedMap.delete(productId);
    removeProductSelectionIds(productId);
    if (activeProductId.value === productId) {
      activeProductId.value = '';
      xrkTableRef.value?.clearSearchValues?.();
      xrkTableRef.value?.initList?.();
    }
    return;
  }
  // 勾选：以产品基础数据为基底（补 scenes 空数组，结构对齐 SceneManageProductItem）+ 空场景
  const product = allProducts.value.find(p => p.productId === productId);
  if (!product) return;
  checkedMap.set(productId, {
    ...JSON.parse(JSON.stringify(product)),
    scenes: []
  });
  if (activeProductId.value !== productId) {
    activeProductId.value = productId;
    // 清空 store 勾选（同 onSwitchProduct：表头「已选 N 项」按全局 selectionIds 计数）
    xrkTableRef.value?.clearSelectionRows?.();
    // 清空 store 勾选（同 onSwitchProduct：表头「已选 N 项」按全局 selectionIds 计数）
    xrkTableRef.value?.clearSelectionRows?.();
    xrkTableRef.value?.clearSearchValues?.();
    xrkTableRef.value?.initList?.();
  }
};

/** 清理某产品的场景勾选（selectionIds 前缀匹配） */
const removeProductSelectionIds = (productId: string) => {
  const prefix = `${productId}_`;
  const rows = xrkTableRef.value?.getSelectionRows?.() || [];
  rows.forEach(r => {
    if (r._id?.startsWith(prefix)) {
      xrkTableRef.value?.removeSelectionRow?.(r._id);
    }
  });
};

/** 场景勾选同步勾选集合：产品自动入集合（UI 勾选样式）+ scenes 维护（已选计数实时）；_id 前缀反推产品 */
const syncSceneChecked = (row: any, checked: boolean) => {
  const productId = String(row._id || '').split('_')[0];
  if (!productId) return;
  let product = checkedMap.get(productId);
  if (checked) {
    // 产品未勾选但勾选场景 → 自动带入勾选集合（左侧勾选样式同步）
    if (!product) {
      const base = allProducts.value.find(p => p.productId === productId);
      if (!base) return;
      product = {
        ...JSON.parse(JSON.stringify(base)),
        scenes: []
      };
      checkedMap.set(productId, product);
    }
    product.scenes = product.scenes || [];
    if (!product.scenes.some(s => s.sceneId === row.sceneId)) {
      product.scenes.push(JSON.parse(JSON.stringify(row)));
    }
  } else {
    if (!product) return;
    product.scenes = (product.scenes || []).filter(
      s => s.sceneId !== row.sceneId
    );
  }
};

/** 全选/清空（当前筛选条件下）：全选=遍历全部数据加入 selectionIds + 同步勾选集合；清空=移除当前产品全部场景勾选（不调接口） */
const onSelectAll = async (checked: boolean) => {
  if (!activeProductId.value) return;
  if (!checked) {
    // 清空：移除当前产品前缀的 selectionIds + 清勾选集合 scenes（selectionRows 更新自动刷新表格，不调接口）
    removeProductSelectionIds(activeProductId.value);
    const product = checkedMap.get(activeProductId.value);
    if (product) {
      product.scenes = [];
    }
    return;
  }
  const search =
    (xrkTableRef.value?.getSearchValue?.() as Record<string, any>) || {};
  // 当前激活产品三属性（匹配覆盖值，无值传 undefined 不序列化）
  const activeProd = allProducts.value.find(
    p => p.productId === activeProductId.value
  );
  sceneLoading.value = true;
  try {
    const allScenes: SceneManageSceneItem[] = [];
    let pageNum = 1;
    let total = 0;
    do {
      const res = await getSceneSceneList({
        productId: activeProductId.value,
        planId: props.planId || undefined,
        industryId: props.industryId || undefined,
        aspId: props.aspId || undefined,
        sceneName: search.sceneName || undefined,
        terminalChannelText: search.terminalChannelText || undefined,
        serviceObjectText: search.serviceObjectText || undefined,
        medicalRepTag: search.medicalRepTag || undefined,
        productCategory: activeProd?.productCategory || undefined,
        productAttribute: activeProd?.productAttribute || undefined,
        lifecycleStage: activeProd?.lifecycleStage || undefined,
        pageNum,
        pageSize: 100
      });
      const data = res?.data;
      allScenes.push(...(data?.scenePage?.list || []));
      total = data?.scenePage?.total || 0;
      pageNum += 1;
    } while (allScenes.length < total && pageNum <= 50);
    allScenes.forEach(scene => {
      const _id = scene._id || `${activeProductId.value}_${scene.sceneId}`;
      xrkTableRef.value?.addSelectionRow?.(_id, { ...scene, _id });
      // addSelectionRow 不触发 selectionAddAfter 钩子：手动同步勾选集合
      syncSceneChecked({ ...scene, _id }, true);
    });
    xrkTableRef.value?.initList?.();
  } catch (error) {
    console.error('全选场景失败:', error);
    XrkMessage.error('全选失败');
  } finally {
    sceneLoading.value = false;
  }
};

const onCancel = () => {
  props.remove();
};

/** 确认：从 XrkTable selectionRows 构造勾选集合（_id 前缀分产品，勾场景自动带产品）+ 手动勾选产品校验；校验失败阻止关闭 */
const onConfirm = () => {
  const rows = xrkTableRef.value?.getSelectionRows?.() || [];

  // 勾选产品集合：场景勾选（rows 按 _id 前缀分组自动带产品）+ 手动勾选（checkedMap，保留无场景的用于校验）
  const productMap = new Map<string, SceneManageProductItem>();
  rows.forEach((row: any) => {
    const productId = String(row._id || '').split('_')[0];
    if (!productId) return;
    let product = productMap.get(productId);
    if (!product) {
      // 产品信息：checkedMap（手动勾选）优先，否则用产品列表基础数据（勾场景自动带）
      const checked = checkedMap.get(productId);
      const base = allProducts.value.find(p => p.productId === productId);
      if (checked) {
        product = JSON.parse(JSON.stringify(checked));
      } else if (base) {
        product = {
          ...JSON.parse(JSON.stringify(base)),
          scenes: []
        };
      } else {
        return;
      }
      productMap.set(productId, product);
    }
    product.scenes = product.scenes || [];
    if (!product.scenes.some(s => s.sceneId === row.sceneId)) {
      product.scenes.push(JSON.parse(JSON.stringify(row)));
    }
  });
  // 手动勾选但未勾场景的产品也进入集合（校验「每个已选产品至少选择一个场景」）
  checkedMap.forEach((p, productId) => {
    if (!productMap.has(productId)) {
      productMap.set(productId, JSON.parse(JSON.stringify(p)));
    }
  });

  const checkedProducts = [...productMap.values()];
  if (checkedProducts.length === 0) {
    XrkMessage.warning('请先在左侧选择至少一个产品');
    return Promise.reject();
  }

  // 校验：每个已选产品至少选择一个场景
  const noSceneProduct = checkedProducts.find(
    p => (p.scenes || []).length === 0
  );
  if (noSceneProduct) {
    XrkMessage.warning('每个已选产品至少选择一个场景');
    return Promise.reject();
  }

  emit('confirm', checkedProducts);
  props.remove();
};
</script>

<style lang="scss" scoped>
.scene-select-dialog {
  :deep(.xrk-dialog__body) {
    padding: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}

.scene-dialog-content {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* ========== 左侧产品面板 ========== */

.product-panel {
  width: 280px;
  height: 100%;
  flex-shrink: 0;
  border-right: 1px solid #e8edf2;
  display: flex;
  flex-direction: column;
  background: #fafbfc;
}

/* BaseAffix fixed 后宽度会塌缩为测量值（弹窗动画期间可能测到 0），用 !important 固定左侧宽度 */
.left-affix {
  width: 280px !important;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 0;
  border-bottom: none;
}

.panel-title {
  font-weight: 600;
  font-size: 14px;
  color: #1c2b25;
}

.product-search {
  box-sizing: border-box;
  width: calc(100% - 32px);
  height: 30px;
  margin: 10px 16px 0;
  padding: 0 9px;
  border: 1px solid #d9e2ee;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
}

.product-list {
  /* 高度固定：占满弹窗当前可用高度（弹窗 85vh 内容区 - 头部/搜索/边距），内部滚动 */
  height: calc(85vh - 210px);
  min-height: 200px;
  overflow-y: auto;
  padding: 10px;
  display: grid;
  gap: 8px;
  align-content: start;
}

.product-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
  font-size: 13px;
  color: #9ca3af;
}

.product-row {
  display: grid;
  grid-template-columns: 24px 1fr auto;
  gap: 8px;
  align-items: center;
  padding: 11px 10px;
  border: 1px solid #e4e8ee;
  border-radius: 7px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #ffc3a5;
  }

  &.active {
    border-color: #ff9f72;
    background: #fff8f4;
  }

  &.checked {
    .product-name {
      color: #ff6a2c;
    }
  }

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    cursor: pointer;
  }
}

.product-info {
  min-width: 0;
}

.product-name {
  font-size: 13px;
  font-weight: 500;
  color: #1c2b25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-sub {
  margin-top: 3px;
  color: #8a9099;
  font-size: 11px;
  line-height: 1.45;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
}

.product-unavailable {
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
}

.product-count {
  font-size: 11px;
  color: #ff6a2c;
  white-space: nowrap;
}

/* ========== 右侧场景面板 ========== */

.scene-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 24px 16px;
}

.scene-empty-hint {
  text-align: left;
  line-height: 1.8;

  b {
    font-size: 14px;
    color: #374151;
  }
}

.scene-toolbar {
  padding: 12px 16px;
  border-bottom: 1px solid #e8edf2;
  flex-shrink: 0;
  text-align: left;
}

.scene-product-name {
  font-weight: 600;
  font-size: 15px;
  color: #1c2b25;
}

.scene-product-meta {
  font-size: 12px;
  color: #6f7782;
  line-height: 1.6;
  margin-top: 2px;

  b {
    color: #374151;
    margin-left: 2px;
    margin-right: 12px;
  }
}

.scene-toolbar-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #e85d1f;
  font-weight: 600;
}

.scene-batch-btns {
  display: inline-flex;
  gap: 8px;
  margin-left: 8px;
}

.scene-table-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* ========== 表格单元格样式 ========== */

.channel-object-cell {
  font-size: 13px;
  line-height: 1.6;
}

.channel-object-sub {
  color: #9aa2ad;
  font-size: 12px;
}

.intro-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.intro-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  line-height: 1.5;
}

.intro-detail-link {
  color: #1677ff;
  font-size: 12px;
  cursor: pointer;
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
  }
}

.history-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.history-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #e7f7ef;
  color: #1f9d6a;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.history-settle {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.history-empty {
  color: #d1d5db;
  font-size: 13px;
}

/* ========== 底部按钮 ========== */

.scene-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 20px;
  border-top: 1px solid #e8edf2;
  flex-shrink: 0;
}
</style>
